import {
  createContext,
  createRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'

export const ContextRef = createRef<FeatureFlags>()

const defaultFeatureFlags = {
  promotions_nav: false,
  push_parking_photo: false,
  dashboards_nav: false,
  parking_controls: false,
  module_qr: false,
}

export type FeatureFlags = typeof defaultFeatureFlags

type FeatureFlagsCtx = [FeatureFlags, (featureFlags: FeatureFlags) => void]
export const FeatureFlagsContext = createContext<FeatureFlagsCtx>([
  { ...defaultFeatureFlags },
  () => {},
])

type Props = {
  children: ReactNode
}

const featureFlagStorageKey = (key: string) => `__feature_flag__.${key}`

// Read a persisted feature flag straight from localStorage. Useful outside the
// React tree (e.g. Firebase messaging setup at boot) where the context/ContextRef
// may not be populated yet.
export function getStoredFeatureFlag(key: keyof FeatureFlags): boolean {
  return localStorage.getItem(featureFlagStorageKey(key)) === '1'
}

function storeFeatureFlags(featureFlags: FeatureFlags) {
  for (const key in featureFlags) {
    const value = featureFlags[key] ? '1' : '0'
    localStorage.setItem(featureFlagStorageKey(key), value)
  }
}

export default ({ children }: Props) => {
  const [featureFlags, setFeatureFlags] = useState(defaultFeatureFlags)

  // https://stackoverflow.com/questions/59232118/how-to-acess-react-context-from-apollo-set-context-http-link
  // Allows us to access this context value in our apollo links
  // outside the react tree.
  useImperativeHandle(ContextRef, () => featureFlags)
  useEffect(() => {
    // This runs on component mount
    const loadedFlags = defaultFeatureFlags
    for (const key in defaultFeatureFlags) {
      loadedFlags[key] = getStoredFeatureFlag(key as keyof FeatureFlags)
    }
    setFeatureFlags(loadedFlags)
  }, [])

  useEffect(() => {
    // This runs on any featureFlag change
    console.log(`updating feature Flags ${JSON.stringify(featureFlags)}`)
    storeFeatureFlags(featureFlags)
  }, [featureFlags])

  return (
    <FeatureFlagsContext.Provider
      value={[
        featureFlags,
        // always create a new object so that useEffect
        // comparisons are easy.
        flags => setFeatureFlags({ ...flags }),
      ]}
    >
      {children}
    </FeatureFlagsContext.Provider>
  )
}
